import React, { useEffect, useMemo, useState } from 'react';
import useApi from '../api';
import { toast } from 'react-toastify';
import toastConfig from '../config/Toast';
import 'react-toastify/dist/ReactToastify.css';
import RowDelete from '../ui/RowDelete';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { useReactTable, getCoreRowModel, getSortedRowModel, flexRender } from '@tanstack/react-table';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { ListChecks } from 'lucide-react';
import { formatRelativeTime } from '../utils/dateFormatter';

interface TaskDefinitionRecord {
  id: string;
  name: string;
  system_name: string;
  instruction: string;
  created_at: string | null;
  updated_at: string | null;
}

const TaskDefinitions: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [taskDefinitions, setTaskDefinitions] = useState<TaskDefinitionRecord[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const { get, remove } = useApi();

  useEffect(() => {
    getTaskDefinitions();
  }, []);

  const getTaskDefinitions = async (search = '') => {
    try {
      setLoading(true);
      const endpoint = search ? `/task-definitions/?search=${encodeURIComponent(search)}` : '/task-definitions/';
      const data = await get(endpoint);
      setTaskDefinitions(data.data);
    } catch (error) {
      console.error('Error fetching task definitions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await remove(`/task-definitions/${id}`);
      setTaskDefinitions((prev) => prev.filter((taskDefinition) => taskDefinition.id !== id));
      toast.success('Task definition deleted globally', toastConfig);
    } catch (error) {
      console.error('Error deleting task definition:', error);
      toast.error('Failed to delete task definition', toastConfig);
    }
  };

  const handleSearch = async (search: string) => {
    setSearchInput(search);
    await getTaskDefinitions(search);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }: any) => <span className="text-sm font-semibold">{row.original.name}</span>,
      },
      {
        accessorKey: 'system_name',
        header: 'System Name',
        cell: ({ row }: any) => <span className="text-sm font-mono">{row.original.system_name}</span>,
      },
      {
        accessorKey: 'instruction',
        header: 'Steps',
        cell: ({ row }: any) => {
          const stepCount = row.original.instruction
            .split('\n')
            .filter((line: string) => line.trim()).length;
          return <span className="text-sm">{stepCount}</span>;
        },
      },
      {
        accessorKey: 'updated_at',
        header: 'Updated',
        cell: ({ row }: any) => (
          <span className="text-sm">
            {row.original.updated_at ? formatRelativeTime(row.original.updated_at) : '-'}
          </span>
        ),
      },
      {
        id: 'actions',
        header: 'Action',
        cell: ({ row }: any) => (
          <RowDelete
            deleteCallback={() => handleDelete(row.original.id)}
            deleteMessage={`Delete "${row.original.name}" globally? This removes it from all agents and cannot be undone.`}
          />
        ),
      },
    ],
    []
  );

  const table = useReactTable({
    data: taskDefinitions,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      sorting: [
        {
          id: 'updated_at',
          desc: true,
        },
      ],
    },
  });

  const TaskDefinitionsLoadingSkeleton = () => (
    <TableBody>
      {[1, 2].map((i) => (
        <TableRow key={i} className="animate-pulse">
          <TableCell>
            <div className="h-4 bg-muted rounded w-[85%] max-w-[220px]" />
          </TableCell>
          <TableCell>
            <div className="h-4 bg-muted rounded w-[70%] max-w-[160px]" />
          </TableCell>
          <TableCell>
            <div className="h-4 bg-muted rounded w-[30%] max-w-[40px]" />
          </TableCell>
          <TableCell>
            <div className="h-4 bg-muted rounded w-[60%] max-w-[90px]" />
          </TableCell>
          <TableCell>
            <div className="h-4 bg-muted rounded w-[50%] max-w-[50px] ml-auto" />
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  );

  return (
    <div className="p-12 overflow-y-auto max-h-[calc(100vh-1px)] scrollbar-thin grow">
      <h1 className="text-4xl font-bold flex items-center gap-2">
        <ListChecks className="h-9 w-9" />
        Task Definitions
      </h1>
      <p className="mt-4 text-sm text-slate-500">
        Manage reusable task definitions in your global knowledge base.
      </p>
      <Separator className="my-4" />
      <div className="mx-auto flex flex-row">
        <Input
          className="w-1/3"
          type="text"
          id="search"
          placeholder="Search task definitions..."
          autoComplete="off"
          value={searchInput}
          onChange={(e: any) => setSearchInput(e.target.value)}
          onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
              handleSearch(e.currentTarget.value);
            }
          }}
        />
      </div>
      <div className="flex flex-col mt-4">
        <div className="-m-1.5 overflow-x-auto pb-12">
          <div className="p-1.5 min-w-full inline-block align-middle">
            <div className="border rounded-lg shadow overflow-hidden dark:border-slate-900">
              <Table>
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <TableHead key={header.id}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                {loading ? (
                  <TaskDefinitionsLoadingSkeleton />
                ) : (
                  <TableBody>
                    {table.getRowModel().rows.length ? (
                      table.getRowModel().rows.map((row) => (
                        <TableRow key={row.id}>
                          {row.getVisibleCells().map((cell) => (
                            <TableCell key={cell.id}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={columns.length} className="h-24 text-center">
                          No task definitions
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                )}
              </Table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskDefinitions;
